"use client";

import { BentoCard } from "@/components/BentoCard";
import { Container } from "@/components/Container";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
} from "@/components/Content";
import {
	SlideUp,
	StaggerContainer,
	StaggerItem,
} from "@/components/animations/page-transitions";
import {
	servicesGraphVideoSource,
	servicesGraphVideoType,
} from "@/constants/assets";
import { cn } from "@/lib/utils";
import type { ImageDetails } from "@/types";
import Image from "next/image";

type Bento = {
	eyebrow: string;
	title: string;
	description: AdaptiveTextProps["portableText"];
	image: ImageDetails;
	className?: string;
};

type Props = {
	title: string;
	text: AdaptiveTextProps["portableText"];
	cards: Bento[];
	subtitle: string;
	subText: AdaptiveTextProps["portableText"];
	id?: string;
};

const BentoSection = ({ id, title, text, cards, subtitle, subText }: Props) => {
	const limitedCards = cards.slice(0, 5);

	return (
		<section id={id} className="relative overflow-hidden">
			<Container>
				<Title as="h2">{title}</Title>
				<AdaptiveText portableText={text} />
			</Container>

			<SlideUp className="relative isolate mx-auto mt-12 max-w-7xl md:mt-16 lg:p-2">
				<video
					autoPlay
					muted
					loop
					controls={false}
					className="rounded-md border-2 border-black/5"
				>
					<source
						src={servicesGraphVideoSource}
						type={servicesGraphVideoType}
					/>
					Your browser does not support the video tag.
				</video>
				<div className="-inset-x-12 absolute top-0 h-px bg-neutral-200" />
				<div className="-inset-x-12 absolute bottom-0 h-px bg-neutral-200" />
				<div className="-inset-y-12 absolute left-0 w-px bg-neutral-200" />
				<div className="-inset-y-12 absolute right-0 w-px bg-neutral-200" />
			</SlideUp>

			<Container>
				<StaggerContainer
					className="mt-16 md:mt-40"
					initialDelay={0.3}
					staggerDelay={0.1}
				>
					<StaggerItem>
						<Title as="h3">{subtitle}</Title>
						<AdaptiveText portableText={subText} />
					</StaggerItem>
				</StaggerContainer>

				<StaggerContainer
					className="mt-16 mb-16 grid grid-cols-1 gap-4 lg:grid-cols-6 lg:grid-rows-2"
					initialDelay={0.3}
					staggerDelay={0.1}
				>
					{limitedCards.map((card, index) => (
						<StaggerItem
							key={index.toString()}
							className={cn({
								"max-lg:rounded-t-4xl lg:col-span-3 lg:rounded-tl-4xl":
									index === 0,
								"lg:col-span-3 lg:rounded-tr-4xl": index === 1,
								"lg:col-span-2 lg:rounded-bl-4xl": index === 2,
								"lg:col-span-2": index === 3,
								"max-lg:rounded-b-4xl lg:col-span-2 lg:rounded-br-4xl":
									index === 4,
							})}
						>
							<BentoCard
								eyebrow={card.eyebrow}
								title={card.title}
								description={card.description}
								index={index}
								graphic={
									<Image
										src={card.image.src}
										alt={card.image.alt || "Memorang"}
										width={1920}
										height={1080}
										className="object-cover object-right"
										quality={100}
										unoptimized
									/>
								}
								fade={["bottom"]}
							/>
						</StaggerItem>
					))}
				</StaggerContainer>
			</Container>
		</section>
	);
};

export default BentoSection;
